import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
  NgZone,
  PLATFORM_ID
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CommunityNewsService } from '../../services/community-news.service';
import { ToastService } from '../../services/toast.service';
import { ErrorMapperService } from '../../services/error-mapper.service';
import { CommunityNews } from '../../interfaces/community-news.interface';
import { LoadingModalComponent } from '../loading-modal/loading-modal.component';
import { fadeInOut } from '../../animations/animations';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

@Component({
  selector: 'app-community-news-list',
  standalone: true,
  imports: [CommonModule, LoadingModalComponent],
  templateUrl: './community-news-list.component.html',
  styleUrls: ['./community-news-list.component.scss'],
  animations: [fadeInOut]
})
export class CommunityNewsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private newsService = inject(CommunityNewsService);
  private toastService = inject(ToastService);
  private errorMapper = inject(ErrorMapperService);
  private zone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  @ViewChildren('newsTitle', { read: ElementRef })
  private newsTitleRefs!: QueryList<ElementRef<HTMLElement>>;

  private newsScrollTriggers: ScrollTrigger[] = [];
  private newsDecodeTweens: gsap.core.Tween[] = [];
  private readonly SCRAMBLE_CHARS = '!<>-_\\/[]{}=+*^?#$%01';

  /** Cap how many items render — used on Home; the dedicated page leaves this unset. */
  @Input() limit?: number;

  /**
   * Word cap applied to each item's body when `limit` is set (i.e. the
   * compact Home preview). The dedicated /community-news page passes no
   * `limit`, so it always renders full, untruncated content.
   */
  @Input() wordLimit = 45;

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.newsTitleRefs.changes.subscribe(() => this.setupNewsTitleDecodes());
    this.setupNewsTitleDecodes();
  }

  ngOnDestroy(): void {
    this.newsScrollTriggers.forEach((trigger) => trigger.kill());
    this.newsDecodeTweens.forEach((tween) => tween.kill());
  }

  news: CommunityNews[] = [];
  loading = true;

  get visibleNews(): CommunityNews[] {
    return this.limit ? this.news.slice(0, this.limit) : this.news;
  }

  /**
   * Truncates a news body to `wordLimit` words, word-boundary safe, with a
   * trailing ellipsis when cut short — only in the Home preview context
   * (where `limit` is set). The full article always lives one click away
   * on the dedicated news page, so home doesn't need to carry the whole
   * body and risk crowding the rest of the page.
   */
  previewText(content: string): string {
    if (!this.limit) return content;

    const words = content.trim().split(/\s+/);
    if (words.length <= this.wordLimit) return content;

    // Ellipsis is rendered separately (see template) so it can carry its
    // own accent styling instead of sitting in plain body-text color.
    return words.slice(0, this.wordLimit).join(' ');
  }

  isTruncated(content: string): boolean {
    if (!this.limit) return false;
    return content.trim().split(/\s+/).length > this.wordLimit;
  }

  async ngOnInit() {
    try {
      this.news = await this.newsService.getPublishedNews();
    } catch (error) {
      const mapped = this.errorMapper.map(error);
      this.toastService.error(mapped.userMessage, 'Error Loading News');
    } finally {
      this.loading = false;
    }
  }

  private setupNewsTitleDecodes(): void {
    if (this.limit) {
      return;
    }

    const titles = this.newsTitleRefs.toArray().map((ref) => ref.nativeElement);
    if (!titles.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.newsScrollTriggers.forEach((trigger) => trigger.kill());
    this.newsDecodeTweens.forEach((tween) => tween.kill());
    this.newsScrollTriggers = [];
    this.newsDecodeTweens = [];

    this.zone.runOutsideAngular(() => {
      titles.forEach((title) => {
        const letters = this.splitTitle(title);
        if (!letters.length) {
          return;
        }

        this.newsScrollTriggers.push(ScrollTrigger.create({
          trigger: title,
          start: 'top 88%',
          onEnter: () => this.runDecodeReveal(letters),
          onEnterBack: () => this.runDecodeReveal(letters)
        }));
      });
    });
  }

  private splitTitle(title: HTMLElement): Array<{ el: HTMLElement; char: string }> {
    const text = title.textContent ?? '';
    if (!text.trim()) {
      return [];
    }

    title.setAttribute('aria-label', text);
    title.textContent = '';

    return Array.from(text, (char) => {
      const element = document.createElement('span');
      element.className = char === ' '
        ? 'decode-letter decode-letter--space'
        : 'decode-letter';
      element.setAttribute('aria-hidden', 'true');
      element.textContent = char;
      title.appendChild(element);
      return { el: element, char };
    });
  }

  private runDecodeReveal(letters: Array<{ el: HTMLElement; char: string }>): void {
    this.newsDecodeTweens.forEach((tween) => tween.kill());
    this.newsDecodeTweens = [];

    letters.forEach(({ el, char }, index) => {
      if (char === ' ') {
        return;
      }

      el.classList.add('is-decoding');
      const state = { t: 0 };
      const tween = gsap.to(state, {
        t: 1,
        duration: 0.5,
        delay: index * 0.035,
        ease: 'none',
        onUpdate: () => {
          el.textContent = state.t < 0.8
            ? this.SCRAMBLE_CHARS[Math.floor(Math.random() * this.SCRAMBLE_CHARS.length)]
            : char;
        },
        onComplete: () => {
          el.textContent = char;
          el.classList.remove('is-decoding');
        }
      });
      this.newsDecodeTweens.push(tween);
    });
  }
}
